import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Building2, MapPin, CalendarClock, FileText, ShieldCheck,
  Package, Users, Calculator, FileSignature, ListChecks, CalendarRange, History,
  ScanSearch, FolderCheck, CheckCircle2, Clock, Sparkles, Lock,
  Upload, Download, Trash2, RefreshCw, ExternalLink, Trophy, ShieldAlert, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDate, fmtDateTime, errMsg } from "@/lib/api";
import { StageBadge, ComplianceBadge } from "@/components/Badges";
import PropostaModule from "@/components/PropostaModule";
import { ItensCotacao } from "@/components/ItensCotacao";

const TABS = [
  { id: "visao", label: "Visão Geral", icon: FolderCheck, fase: 1 },
  { id: "edital", label: "Análise do Edital", icon: ScanSearch, fase: 3 },
  { id: "compliance", label: "Compliance", icon: ShieldAlert, fase: 3 },
  { id: "habilitacao", label: "Habilitação", icon: ShieldCheck, fase: 2 },
  { id: "documentos", label: "Documentos", icon: FileText, fase: 2 },
  { id: "itens", label: "Itens da Licitação", icon: Package, fase: 1 },
  { id: "fornecedores", label: "Fornecedores", icon: Users, fase: 4 },
  { id: "precificacao", label: "Precificação", icon: Calculator, fase: 4 },
  { id: "proposta", label: "Proposta", icon: FileSignature, fase: 4 },
  { id: "checklist", label: "Checklist", icon: ListChecks, fase: 1 },
  { id: "cronograma", label: "Cronograma", icon: CalendarRange, fase: 1 },
  { id: "resultado", label: "Resultado", icon: Trophy, fase: 5 },
  { id: "historico", label: "Histórico", icon: History, fase: 1 },
];

const RESULT_STATUS = ["EM ANDAMENTO", "1º COLOCADO", "EM HABILITAÇÃO", "DECLARADO VENCEDOR", "ADJUDICADO", "HOMOLOGADO", "PERDIDA", "DESCLASSIFICADA", "INABILITADA", "CANCELADA"];
const LOSS_MOTIVOS = ["Preço", "Documentação", "Inabilitação", "Desclassificação", "Especificação", "Prazo", "Não participação", "Outro"];
const WIN_STATUS = ["DECLARADO VENCEDOR", "ADJUDICADO", "HOMOLOGADO"];
const LOSS_STATUS = ["PERDIDA", "DESCLASSIFICADA", "INABILITADA"];

const DOT = { verde: "bg-emerald-500", amarelo: "bg-amber-500", vermelho: "bg-rose-500", laranja: "bg-orange-500", cinza: "bg-slate-400" };
const DOT_TEXT = { verde: "text-emerald-700", amarelo: "text-amber-700", vermelho: "text-rose-700", laranja: "text-orange-700", cinza: "text-slate-500" };
const HAB_LABEL = { verde: "ATENDIDO", amarelo: "COM RESSALVA", vermelho: "NÃO ATENDIDO", laranja: "PENDENTE", cinza: "N/A" };

const ORIGEM = {
  pncp: ["📄 PNCP", "bg-sky-50 text-sky-700"],
  edital: ["📄 Edital", "bg-indigo-50 text-indigo-700"],
  termo_referencia: ["📄 TR", "bg-indigo-50 text-indigo-700"],
  ia: ["🤖 IA", "bg-violet-50 text-violet-700"],
  manual: ["👤 Manual", "bg-amber-50 text-amber-700"],
  manual_edit: ["✏️ Editado", "bg-amber-50 text-amber-700"],
};
const ITEM_FORM = [
  ["numero_item", "Número do item"], ["codigo", "Código"], ["descricao", "Descrição", "col-span-2"],
  ["quantidade", "Quantidade"], ["unidade", "Unidade de fornecimento"],
  ["valor_unitario", "Valor unitário estimado"], ["valor_total", "Valor total estimado"],
  ["marca_exigida", "Marca exigida"], ["marca_referencia", "Marca de referência"],
  ["fabricante", "Fabricante"], ["modelo", "Modelo / referência"],
  ["especificacoes", "Especificações técnicas", "col-span-2"], ["dimensoes", "Dimensões"],
  ["material", "Material"], ["cor", "Cor"], ["capacidade", "Capacidade"],
  ["embalagem", "Embalagem"], ["certificacoes", "Certificações"], ["prazo_entrega", "Prazo de entrega"],
  ["observacoes", "Observações", "col-span-2"], ["referencia_fonte", "Referência da fonte (ex.: TR item 7.3 pág 18)", "col-span-2"],
];
const FONTE_OPCOES = ["Edital", "Termo de Referência", "Anexo", "Planilha", "Documento complementar", "Outra fonte"];
const MODELO = {
  entrega_direta: ["🟢 Entrega Direta", "bg-emerald-50 text-emerald-700"],
  estoque_proprio: ["🔵 Estoque Próprio", "bg-sky-50 text-sky-700"],
  a_definir: ["🟡 A definir", "bg-amber-50 text-amber-700"],
};
const SPEC_STATUS = {
  verde: ["🟢 Especificação OK", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  amarelo: ["🟡 Parcial / ambíguo", "bg-amber-50 text-amber-700 border-amber-200"],
  vermelho: ["🔴 Restritivo / direcionado", "bg-rose-50 text-rose-700 border-rose-200"],
  atencao: ["⚠️ Não localizado", "bg-orange-50 text-orange-700 border-orange-200"],
  error: ["⚠️ Falha na análise", "bg-slate-100 text-slate-600 border-slate-200"],
};

function FichaTecnica({ entry }) {
  const f = entry?.ficha || {};
  const atrs = f.atributos || [];
  const pts = f.pontos_atencao || [];
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="ws-item-ficha">
      <div className="flex flex-wrap items-center gap-2">
        {(() => { const [lbl, cls] = SPEC_STATUS[f.status] || SPEC_STATUS.atencao; return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{lbl}</span>; })()}
        {f.marca_modelo_exigido && <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Exige marca/modelo específico</span>}
        {entry?.com_edital === false && <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-500">Sem edital anexado — baseado só na descrição</span>}
      </div>
      {f.resumo && <p className="text-xs text-slate-600">{f.resumo}</p>}
      {atrs.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <tr><th className="p-1.5">Atributo</th><th className="p-1.5">Exigido</th><th className="p-1.5">Faixa</th><th className="p-1.5">Obrig.</th><th className="p-1.5">Fonte</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {atrs.map((a, i) => (
                <tr key={i}>
                  <td className="p-1.5 font-medium text-slate-700">{a.atributo}</td>
                  <td className="p-1.5 text-slate-600">{a.exigido || "—"}{a.unidade ? ` ${a.unidade}` : ""}</td>
                  <td className="p-1.5 font-mono text-slate-500">{a.operador && a.operador !== "—" ? `${a.operador} ` : ""}{a.valor_min ?? ""}{a.valor_max != null ? `–${a.valor_max}` : ""}</td>
                  <td className="p-1.5">{a.obrigatorio ? "✔" : "—"}</td>
                  <td className="p-1.5"><Fonte fonte={a.fonte} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-xs italic text-slate-400">Nenhum atributo localizado no edital — leitura manual recomendada.</p>}
      {pts.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-semibold text-slate-600">Pontos de atenção</div>
          {pts.map((p, i) => (
            <div key={i} className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">⚠️ {p.texto}<Fonte fonte={p.fonte} /></div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400">Ficha enriquecida pela IA a partir do edital/TR. Os dados originais do PNCP permanecem preservados acima. Confirme sempre no edital — a IA nunca inventa especificações.</p>
    </div>
  );
}
const STATUS_LOG = ["Pendente", "Cotação", "Pedido realizado", "Em preparação", "Enviado", "Em trânsito", "Entregue", "Problema na entrega"];
const CANAIS = ["Fabricante", "Distribuidor", "Atacadista", "Loja física", "Marketplace", "E-commerce", "Importador", "Outro"];
const FORN_DIRETA = [["endereco_entrega", "Endereço de entrega do órgão"], ["responsavel", "Responsável pela entrega"], ["contato_local", "Contato no local"], ["data_prevista", "Data prevista"], ["prazo_maximo", "Prazo máximo (dias)"], ["transportadora", "Transportadora"], ["rastreamento", "Rastreamento"], ["obs", "Observações"]];
const FORN_ESTOQUE = [["data_compra", "Data da compra"], ["numero_pedido", "Nº do pedido"], ["qtd_comprada", "Qtd comprada"], ["custo_unitario", "Custo unitário"], ["frete", "Frete"], ["previsao_chegada", "Previsão de chegada (dias)"], ["data_recebimento", "Data de recebimento"], ["qtd_recebida", "Qtd recebida"], ["local_armazenamento", "Local de armazenamento"], ["data_envio", "Data de envio ao órgão"], ["transportadora", "Transportadora"], ["rastreamento", "Rastreamento"], ["estoque_reservado", "Estoque reservado (qtd)"], ["obs", "Observações"]];
const extractDays = (s) => { const m = String(s ?? "").match(/(\d+)/); return m ? parseInt(m[1], 10) : null; };
const fornType = (k) => (k.startsWith("data") || k === "previsao_chegada" ? "date" : (k.includes("custo") || k.includes("frete") || k.includes("qtd") || k.includes("prazo_maximo") || k.includes("reservado") ? "number" : "text"));

const CAMPO_LABELS = {
  orgao: "Órgão", processo: "Processo", numero_contratacao: "Nº da contratação", modalidade: "Modalidade",
  objeto: "Objeto", criterio_julgamento: "Critério de julgamento", forma_disputa: "Forma de disputa",
  data_hora_sessao: "Data/hora da sessão", prazo_proposta: "Prazo para proposta", validade_proposta: "Validade da proposta",
  valor_estimado: "Valor estimado", local_entrega: "Local de entrega", prazo_entrega: "Prazo de entrega",
  forma_pagamento: "Forma de pagamento", garantia: "Garantia", amostra: "Amostra", catalogo: "Catálogo",
};

function Fonte({ fonte }) {
  const [open, setOpen] = useState(false);
  if (!fonte || (!fonte.arquivo && !fonte.pagina && !fonte.trecho)) return null;
  return (
    <span className="mt-1 block">
      <button onClick={() => setOpen(!open)} className="text-[10px] font-semibold text-sky-600 hover:underline">
        📄 {fonte.arquivo || "edital"}{fonte.pagina ? ` · pág ${fonte.pagina}` : ""} · ver trecho
      </button>
      {open && fonte.trecho && <span className="mt-1 block max-w-md rounded bg-slate-50 p-2 text-[11px] italic text-slate-600">“{fonte.trecho}”</span>}
    </span>
  );
}

function Ring({ percent }) {
  const r = 34, c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  const color = percent >= 70 ? "#10b981" : percent >= 40 ? "#0284C7" : "#f59e0b";
  return (
    <div className="relative grid h-24 w-24 place-items-center" data-testid="readiness-ring">
      <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-display text-xl font-bold text-slate-900">{percent}%</div>
      </div>
    </div>
  );
}

function ComingSoon({ title, desc, fase }) {
  return (
    <div className="grid place-items-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="ws-coming-soon">
      <div className="grid h-12 w-12 place-items-center rounded-xl bg-sky-50"><Lock className="h-6 w-6 text-sky-600" /></div>
      <h3 className="mt-3 font-display text-lg font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-md text-sm text-slate-500">{desc}</p>
      <span className="mt-3 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">DISPONÍVEL NA FASE {fase}</span>
    </div>
  );
}

export default function Participar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("visao");
  const [saving, setSaving] = useState(false);
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const [analyzingHab, setAnalyzingHab] = useState(false);
  const [uploadingEdital, setUploadingEdital] = useState(false);
  const [fornModal, setFornModal] = useState(null);
  const [prec, setPrec] = useState({});
  const [genProp, setGenProp] = useState(false);
  const [declTitulo, setDeclTitulo] = useState("");
  const [genDecl, setGenDecl] = useState(false);
  const [openVer, setOpenVer] = useState(null);
  const [verText, setVerText] = useState("");
  const precInit = useRef(false);
  const [resultado, setResultado] = useState({});
  const resInit = useRef(false);
  const [itemModal, setItemModal] = useState(null);
  const [dupWarn, setDupWarn] = useState(false);
  const [fornCfg, setFornCfg] = useState({});
  const fornInit = useRef(false);
  const [fornEdit, setFornEdit] = useState(null);
  const [expandedSpec, setExpandedSpec] = useState(null);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [genCot, setGenCot] = useState(false);
  const [allSups, setAllSups] = useState([]);
  useEffect(() => {
    if (fornModal && allSups.length === 0) { api.get("/suppliers").then((r) => setAllSups(r.data)).catch(() => {}); }
  }, [fornModal, allSups.length]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/opportunities/${id}/workspace`);
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const editalSt = data?.workspace?.edital_analysis?.status;
  const propSt = data?.workspace?.proposta?.status;
  const complSt = data?.workspace?.compliance?.status;
  const specProcessing = data?.workspace?.item_specs
    ? Object.values(data.workspace.item_specs).some((s) => s?.status === "processing")
    : false;
  useEffect(() => {
    if (editalSt === "processing" || propSt === "processing" || complSt === "processing" || specProcessing) {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [editalSt, propSt, complSt, specProcessing, load]);

  useEffect(() => {
    if (data && !precInit.current) { setPrec(data.workspace?.precificacao?.itens || {}); precInit.current = true; }
  }, [data]);

  useEffect(() => {
    if (data && !resInit.current) { setResultado(data.workspace?.resultado || {}); resInit.current = true; }
  }, [data]);

  useEffect(() => {
    if (data && !fornInit.current) { setFornCfg(data.workspace?.item_fornecimento || {}); fornInit.current = true; }
  }, [data]);

  const patchWs = async (body) => {
    setSaving(true);
    try {
      const { data: d } = await api.patch(`/opportunities/${id}/workspace`, body);
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const toggleCheck = (i) => {
    const cl = data.workspace.checklist.map((c, idx) => idx === i ? { ...c, done: !c.done } : c);
    patchWs({ checklist: cl, _action: `Checklist: "${cl[i].label}" ${cl[i].done ? "concluído" : "reaberto"}` });
  };
  const toggleEtapa = (i) => {
    const cr = data.workspace.cronograma.map((c, idx) => idx === i ? { ...c, done: !c.done } : c);
    patchWs({ cronograma: cr });
  };
  const setItemStatus = (num, status) => {
    const ia = { ...(data.workspace.item_analysis || {}) };
    const cur = ia[String(num)] || {};
    ia[String(num)] = { ...cur, status, confirmed: status === "verde" };
    patchWs({ item_analysis: ia, _action: `Item ${num} marcado como ${status === "verde" ? "compatível" : status === "amarelo" ? "necessita análise" : "não localizado"}` });
  };
  const changeStage = async (stage) => {
    try {
      await api.patch(`/opportunities/${id}`, { stage });
      toast.success(`Status do Pipeline alterado para ${stage}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const analyzeHab = async () => {
    setAnalyzingHab(true);
    try {
      const { data: d } = await api.post(`/opportunities/${id}/habilitacao/analyze`);
      setData(d);
      toast.success("Matriz de habilitação gerada pela IA e cruzada com o Cofre");
    } catch (e) { toast.error(errMsg(e)); } finally { setAnalyzingHab(false); }
  };
  const setHabStatus = (i, status) => {
    const hab = data.workspace.habilitacao;
    const rows = hab.rows.map((r, idx) => idx === i ? { ...r, status } : r);
    patchWs({ habilitacao: { ...hab, rows } });
  };
  const uploadEdital = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEdital(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/opportunities/${id}/edital-files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Arquivo enviado");
      load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setUploadingEdital(false); }
  };
  const deleteEdital = async (fid) => {
    try { await api.delete(`/opportunities/${id}/edital-files/${fid}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const analyzeEdital = async () => {
    try {
      await api.post(`/opportunities/${id}/edital/analyze`);
      toast.info("Análise iniciada — pode levar cerca de 1 minuto");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const habFromEdital = async () => {
    try {
      const { data: d } = await api.post(`/opportunities/${id}/habilitacao/from-edital`);
      setData(d); setTab("habilitacao");
      toast.success("Matriz de habilitação gerada a partir do edital");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [fetchingPncp, setFetchingPncp] = useState(false);
  const fetchPncp = async () => {
    setFetchingPncp(true);
    try {
      const { data: r } = await api.post(`/opportunities/${id}/edital/fetch-pncp`);
      if (r.added > 0) toast.success(`${r.added} documento(s) recuperado(s) do PNCP`);
      else if (r.fallback) toast.warning("⚠️ Documento não localizado automaticamente no PNCP. Importe manualmente abaixo.");
      else toast.info("Nenhum documento novo no PNCP (os anexos já estão aqui).");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setFetchingPncp(false); }
  };
  const resetEdital = async () => {
    try {
      await api.patch(`/opportunities/${id}/workspace`, { edital_analysis: null });
      await api.post(`/opportunities/${id}/edital/analyze`);
      toast.info("Análise reiniciada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveForn = async () => {
    if (!fornModal.nome) { toast.error("Informe o nome do fornecedor"); return; }
    try {
      const b = fornModal;
      if (b.id) { const { data: d } = await api.put(`/opportunities/${id}/fornecedores/${b.id}`, b); setData(d); }
      else { const { data: d } = await api.post(`/opportunities/${id}/fornecedores`, b); setData(d); }
      toast.success("Cotação salva"); setFornModal(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delForn = async (fid) => {
    try { const { data: d } = await api.delete(`/opportunities/${id}/fornecedores/${fid}`); setData(d); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const [analisando, setAnalisando] = useState(false);
  const analisarLink = async () => {
    if (!fornModal.link_produto?.trim()) { toast.error("Cole o link do produto"); return; }
    setAnalisando(true);
    try {
      let fid = fornModal.id;
      if (!fid) {
        const { data: d } = await api.post(`/opportunities/${id}/fornecedores`, fornModal);
        setData(d);
        const created = (d.workspace?.fornecedores || []).slice(-1)[0];
        fid = created?.id;
        setFornModal((m) => ({ ...m, id: fid }));
      }
      const { data: d2 } = await api.post(`/opportunities/${id}/fornecedores/${fid}/analisar-link`, { url: fornModal.link_produto });
      setData(d2);
      const uf = (d2.workspace?.fornecedores || []).find((x) => x.id === fid);
      if (uf) setFornModal((m) => ({ ...m, id: fid, analise_link: uf.analise_link, link_produto: uf.link_produto }));
      toast.success("Análise concluída");
    } catch (e) { toast.error(errMsg(e)); } finally { setAnalisando(false); }
  };
  const usarProduto = () => {
    const ex = fornModal.analise_link?.extraidos || {};
    setFornModal((m) => ({
      ...m,
      marca_referencia: ex.marca || m.marca_referencia, fabricante: ex.fabricante || m.fabricante,
      modelo: ex.modelo || m.modelo, especificacoes_tecnicas: ex.especificacoes || m.especificacoes_tecnicas,
      dimensoes: ex.dimensoes || m.dimensoes, material: ex.material || m.material, cor: ex.cor || m.cor,
      capacidade: ex.capacidade || m.capacidade, embalagem: ex.embalagem || m.embalagem,
      produto: ex.nome || m.produto,
    }));
    toast.success("Campos preenchidos — revise antes de salvar");
  };
  const savePrec = async () => {
    try {
      const { data: d } = await api.patch(`/opportunities/${id}/workspace`, { precificacao: { ...(data.workspace?.precificacao || {}), itens: prec }, _action: "Precificação atualizada" });
      setData(d); setPrec(d.workspace?.precificacao?.itens || {}); toast.success("Precificação salva");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const gerarProposta = async () => {
    setGenProp(true);
    try { await api.post(`/opportunities/${id}/proposta/generate`); toast.info("Gerando proposta… pode levar até 1 minuto"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setGenProp(false); }
  };
  const gerarDeclaracao = async () => {
    if (!declTitulo.trim()) { toast.error("Informe o título da declaração"); return; }
    setGenDecl(true);
    try { const { data: d } = await api.post(`/opportunities/${id}/declaracoes/generate`, { titulo: declTitulo }); setData(d); setDeclTitulo(""); toast.success("Rascunho gerado"); }
    catch (e) { toast.error(errMsg(e)); } finally { setGenDecl(false); }
  };
  const delDeclaracao = async (did) => {
    try { const { data: d } = await api.delete(`/opportunities/${id}/declaracoes/${did}`); setData(d); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveVersao = async (vid) => {
    const prop = data.workspace.proposta;
    const versoes = prop.versoes.map((v) => v.id === vid ? { ...v, conteudo: verText } : v);
    try {
      const { data: d } = await api.patch(`/opportunities/${id}/workspace`, { proposta: { ...prop, versoes }, _action: "Proposta editada" });
      setData(d); setOpenVer(null); toast.success("Proposta salva");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setR = (k, v) => setResultado((r) => ({ ...r, [k]: v }));
  const saveResultado = async () => {
    try { const { data: d } = await api.patch(`/opportunities/${id}/workspace`, { resultado, _action: "Resultado registrado" }); setData(d); toast.success("Resultado salvo"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const converterComercial = async () => {
    try { const { data: d } = await api.post(`/opportunities/${id}/converter-comercial`); setData(d); toast.success("Convertida em operação comercial — contas a receber criado no Financeiro"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleContratoEtapa = async (i) => {
    const c = data.workspace.contrato;
    const etapas = c.etapas.map((e, idx) => idx === i ? { ...e, done: !e.done } : e);
    try { const { data: d } = await api.patch(`/opportunities/${id}/workspace`, { contrato: { ...c, etapas } }); setData(d); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openEditItem = (m) => {
    setDupWarn(false);
    if (m.manual_id) {
      const mi = (data.workspace.manual_itens || []).find((x) => x.id === m.manual_id) || {};
      setItemModal({ ...mi });
    } else {
      setItemModal({ _override_numero: m.numero_item, numero_item: m.numero_item, descricao: m.descricao, quantidade: m.quantidade, unidade: m.unidade, valor_unitario: m.valor_unitario, valor_total: m.valor_total });
    }
  };
  const saveItem = async (force = false) => {
    const f = itemModal;
    if (!f.descricao) { toast.error("Informe a descrição do item"); return; }
    if (!f.id && !f._override_numero && !force) {
      const dup = (data.item_matches || []).some((m) => String(m.numero_item) === String(f.numero_item) || (f.descricao && m.descricao && m.descricao.toLowerCase().trim() === f.descricao.toLowerCase().trim()));
      if (dup) { setDupWarn(true); return; }
    }
    try {
      let d;
      if (f._override_numero) ({ data: d } = await api.post(`/opportunities/${id}/itens/${f._override_numero}/override`, f));
      else if (f.id) ({ data: d } = await api.put(`/opportunities/${id}/itens-manuais/${f.id}`, f));
      else ({ data: d } = await api.post(`/opportunities/${id}/itens-manuais`, f));
      setData(d); setItemModal(null); setDupWarn(false); toast.success("Item salvo");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delItem = async (m) => {
    if (!m.manual_id) return;
    try { const { data: d } = await api.delete(`/opportunities/${id}/itens-manuais/${m.manual_id}`); setData(d); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const analyzeSpecs = async (num) => {
    try {
      await api.post(`/opportunities/${id}/itens/${num}/analyze-specs`);
      toast.success("Analisando especificações do item…");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const analyzeAllSpecs = async () => {
    setAnalyzingAll(true);
    try {
      const { data: r } = await api.post(`/opportunities/${id}/itens/analyze-all`);
      toast.success(`Analisando especificações de ${r.total} item(ns)…`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setAnalyzingAll(false); }
  };
  const analyzeCompliance = async () => {
    try {
      await api.post(`/opportunities/${id}/compliance/analyze`);
      toast.success("Analisando compliance operacional…");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const gerarCotacao = async () => {
    setGenCot(true);
    try {
      const { data: r } = await api.post(`/opportunities/${id}/cotacao/gerar`);
      toast.success(`Cotação ${r.cotacao?.nome} gerada`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setGenCot(false); }
  };
  const setCotacaoStatus = async (cid, status) => {
    try { await api.patch(`/opportunities/${id}/cotacao/${cid}`, { status }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openForn = (numero) => setFornEdit({ numero: String(numero), draft: { ...(fornCfg[String(numero)] || { modelo: "a_definir" }) } });
  const setFD = (k, v) => setFornEdit((fe) => ({ ...fe, draft: { ...fe.draft, [k]: v } }));
  const usarFornecedor = (f) => setFornEdit((fe) => ({ ...fe, draft: { ...fe.draft, fornecedor_id: f.id, fornecedor_nome: f.nome } }));
  const saveForn2 = async () => {
    const map = { ...fornCfg, [fornEdit.numero]: fornEdit.draft };
    try {
      const { data: d } = await api.patch(`/opportunities/${id}/workspace`, { item_fornecimento: map, _action: `Fornecimento do item ${fornEdit.numero} definido` });
      setData(d); setFornCfg(map); setFornEdit(null); toast.success("Fornecimento salvo");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>;

  const o = data.opportunity, ws = data.workspace, ov = data.overview, rd = data.readiness;
  const prazoDate = o.data_encerramento_proposta ? new Date(o.data_encerramento_proposta) : null;
  const diasRestantes = prazoDate ? Math.ceil((prazoDate - new Date()) / 86400000) : null;
  const canSuggest = rd.percent >= 40 && ["NOVA", "ANALISAR", "FAVORITA"].includes(o.stage) && !dismissedSuggestion;

  return (
    <div className="animate-fade-up pb-16">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} data-testid="ws-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        {o.link_oficial && (
          <a href={o.link_oficial} target="_blank" rel="noreferrer" data-testid="ws-link-pncp"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-sky-700 hover:bg-sky-50">
            <ExternalLink className="h-4 w-4" /> Abrir edital no PNCP
          </a>
        )}
      </div>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">{o.modalidade_nome}</span>
              <StageBadge stage={o.stage} />
              {ws.participating && <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Participação ativa</span>}
            </div>
            <h1 className="font-display text-xl font-bold leading-snug text-slate-900">{o.orgao_razao_social}</h1>
            <p className="mt-1 text-sm text-slate-600">{o.objeto}</p>
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-500 sm:grid-cols-3">
              <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {o.unidade_nome || "—"}</span>
              <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Proc. {o.processo || "—"}</span>
              <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Nº {o.numero_compra || "—"}</span>
              {o.unidade_codigo && <span data-testid="ws-uasg" className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> UASG {o.unidade_codigo} · Compra {String(o.numero_compra || "").padStart(5, "0")}/{o.ano_compra}</span>}
              {o.link_sistema_origem && <a data-testid="ws-link-origem" href={o.link_sistema_origem} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sky-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Abrir no {o.sistema_origem || "sistema de origem"}</a>}
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {o.uf} / {o.municipio || "—"}</span>
              <span className="flex items-center gap-1.5 font-semibold text-slate-700">{brl(o.valor_estimado)}</span>
              <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Abertura {fmtDate(o.data_publicacao)}</span>
            </div>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-slate-100 bg-slate-50 px-5 py-3">
            <Ring percent={rd.percent} />
            <div className="mt-1 text-center">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Prontidão</div>
              <div className="text-[10px] text-slate-400">indicador interno — não garante habilitação</div>
            </div>
          </div>
        </div>
        {prazoDate && (
          <div className={`mt-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${diasRestantes <= 3 ? "bg-rose-50 text-rose-700" : diasRestantes <= 7 ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
            <Clock className="h-4 w-4" />
            Prazo para envio da proposta: {fmtDateTime(o.data_encerramento_proposta)}
            {diasRestantes != null && <span>· {diasRestantes >= 0 ? `${diasRestantes} dia(s) restante(s)` : "prazo encerrado"}</span>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`ws-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium ${tab === t.id ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "visao" && (
          <div className="grid gap-4 lg:grid-cols-3" data-testid="ws-visao">
            {(() => {
              const est = { entrega_direta: 0, estoque_proprio: 0, a_definir: 0 };
              const alertas = [];
              const edDias = extractDays((((ws.edital_analysis || {}).result || {}).campos || {}).prazo_entrega?.valor);
              (data.item_matches || []).forEach((m) => {
                const num = String(m.numero_item); const cfg = fornCfg[num] || {};
                const md = cfg.modelo && cfg.modelo !== "a_definir" ? cfg.modelo : "a_definir";
                est[md] = (est[md] || 0) + 1;
                if (md === "a_definir") alertas.push({ n: "amarelo", t: `Item ${num}: fornecimento a definir` });
                if (!cfg.fornecedor_id) alertas.push({ n: "amarelo", t: `Item ${num}: sem fornecedor selecionado` });
                if (cfg.modelo === "estoque_proprio") {
                  if (!cfg.data_compra) alertas.push({ n: "vermelho", t: `Item ${num}: estoque próprio sem compra realizada` });
                  else if (!cfg.data_recebimento) alertas.push({ n: "amarelo", t: `Item ${num}: comprado, ainda não recebido` });
                  else if (!cfg.data_envio) alertas.push({ n: "amarelo", t: `Item ${num}: recebido, ainda não enviado ao órgão` });
                }
                const spd = extractDays(cfg.prazo_maximo || cfg.previsao_chegada);
                if (edDias && spd && spd > edDias) alertas.push({ n: "vermelho", t: `Item ${num}: prazo do fornecedor (${spd}d) maior que o do edital (${edDias}d) — risco logístico` });
              });
              return (
                <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-estrategia">
                  <div className="mb-2 text-sm font-semibold text-slate-800">Estratégia de Fornecimento</div>
                  <div className="flex flex-wrap gap-2 text-xs font-semibold">
                    <span className="rounded-lg bg-emerald-50 px-3 py-1.5 text-emerald-700">🟢 {est.entrega_direta} entrega direta</span>
                    <span className="rounded-lg bg-sky-50 px-3 py-1.5 text-sky-700">🔵 {est.estoque_proprio} estoque próprio</span>
                    <span className="rounded-lg bg-amber-50 px-3 py-1.5 text-amber-700">🟡 {est.a_definir} a definir</span>
                  </div>
                  {alertas.length > 0 && (
                    <ul className="mt-3 space-y-1" data-testid="ws-alertas">
                      {alertas.slice(0, 8).map((a, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-slate-600"><span className={`h-2 w-2 rounded-full ${DOT[a.n]}`} />⚠️ {a.t}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}
            <div className="space-y-4 lg:col-span-2">
              {canSuggest && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="ws-stage-suggestion">
                  <div className="flex items-center gap-2 text-sm font-semibold text-sky-800"><Sparkles className="h-4 w-4" /> Sugestão de status</div>
                  <p className="mt-1 text-sm text-sky-700">Você já avançou na preparação. Deseja mover o status do Pipeline para <strong>DOCUMENTAÇÃO</strong>? (você continua no controle)</p>
                  <div className="mt-2 flex gap-2">
                    <button data-testid="ws-suggest-yes" onClick={() => changeStage("DOCUMENTAÇÃO")} className="rounded-lg bg-sky-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-sky-700">Sim, alterar</button>
                    <button data-testid="ws-suggest-no" onClick={() => setDismissedSuggestion(true)} className="rounded-lg border border-sky-200 px-4 py-1.5 text-sm font-semibold text-sky-700">Agora não</button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Itens", ov.itens_total], ["Itens possíveis", ov.itens_possiveis],
                  ["Itens pendentes", ov.itens_pendentes],
                  ["Checklist", `${rd.checklist_done}/${rd.checklist_total}`],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-slate-200 bg-white p-3 text-center">
                    <div className="font-display text-2xl font-bold text-slate-900">{v}</div>
                    <div className="text-[11px] font-semibold uppercase text-slate-400">{l}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 text-sm font-semibold text-slate-700">O que falta para eu participar?</div>
                {ov.pendencias.length === 0 ? (
                  <p className="text-sm text-emerald-700">Nenhuma pendência identificada nesta fase. 🎉</p>
                ) : (
                  <ul className="space-y-2" data-testid="ws-pendencias">
                    {ov.pendencias.map((p, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <span className={`h-2.5 w-2.5 rounded-full ${DOT[p.nivel]}`} />{p.texto}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-semibold uppercase text-slate-400">Próxima ação recomendada</div>
                <div className="mt-1 text-sm font-semibold text-slate-800">{ov.proxima_acao}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-semibold uppercase text-slate-400">Status no Pipeline</div>
                <div className="mt-1"><StageBadge stage={o.stage} /></div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
                Etapas de <strong>Documentos, Cotação, Precificação e Proposta</strong> serão liberadas nas próximas fases.
              </div>
            </div>
          </div>
        )}

        {tab === "itens" && (
          <div data-testid="ws-itens">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-slate-500">Itens importados automaticamente + itens adicionados manualmente. A origem de cada item é rastreável.</p>
              <div className="flex items-center gap-2">
                <button data-testid="ws-item-analyze-all" onClick={analyzeAllSpecs} disabled={analyzingAll || specProcessing || !data.item_matches?.length} className="flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50">
                  {(analyzingAll || specProcessing) ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />} Analisar especificações (todos){data.item_matches?.length > 40 ? " · 40 por vez" : ""}
                </button>
                <button data-testid="ws-item-add" onClick={() => { setDupWarn(false); setItemModal({ origem: "manual", fonte: "Edital" }); }} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">+ Adicionar item</button>
              </div>
            </div>
            {(!data.item_matches || data.item_matches.length === 0) ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                Nenhum item ainda. Use "+ Adicionar item" se o PNCP/edital não trouxe os itens.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="p-2">#</th><th className="p-2">Origem</th><th className="p-2">Descrição</th>
                      <th className="p-2 text-right">Qtd</th><th className="p-2 text-right">Vlr unit.</th>
                      <th className="p-2">Produto sugerido</th><th className="p-2">Compatibilidade</th><th className="p-2">Ficha técnica</th><th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.item_matches.map((m) => {
                      const [olbl, ocls] = ORIGEM[m.origem] || ORIGEM.pncp;
                      const spec = (ws.item_specs || {})[String(m.numero_item)];
                      const open = String(expandedSpec) === String(m.numero_item);
                      return (
                      <React.Fragment key={m.manual_id || m.numero_item}>
                      <tr data-testid="ws-item-row" className="hover:bg-slate-50">
                        <td className="p-2 text-slate-400">{m.numero_item}</td>
                        <td className="p-2"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${ocls}`}>{olbl}</span></td>
                        <td className="p-2 text-slate-700">{m.descricao}{m.referencia_fonte && <div className="text-[10px] text-slate-400">Fonte: {m.referencia_fonte}</div>}{(() => { const cfg = fornCfg[String(m.numero_item)]; if (cfg?.modelo) { const mv = MODELO[cfg.modelo]; return <div className="mt-0.5"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${mv[1]}`}>{mv[0]}</span>{cfg.fornecedor_nome && <span className="ml-1 text-[10px] text-slate-400">· {cfg.fornecedor_nome}</span>}</div>; } return null; })()}</td>
                        <td className="p-2 text-right font-mono">{m.quantidade} {m.unidade}</td>
                        <td className="p-2 text-right font-mono">{brl(m.valor_unitario)}</td>
                        <td className="p-2 text-xs text-slate-500">{m.sugestao.product_desc || <span className="text-slate-300">— não localizado —</span>}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`h-2.5 w-2.5 rounded-full ${DOT[m.status_efetivo]}`} />
                            <div className="flex gap-1">
                              {[["verde", "Compatível"], ["amarelo", "Analisar"], ["vermelho", "Não"]].map(([s, lbl]) => (
                                <button key={s} data-testid={`ws-item-${m.numero_item}-${s}`} onClick={() => setItemStatus(m.numero_item, s)}
                                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${m.status_efetivo === s ? `${DOT[s]} text-white` : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{lbl}</button>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="p-2">
                          {spec?.status === "processing" ? (
                            <span className="flex items-center gap-1 text-[11px] text-violet-600"><Loader2 className="h-3 w-3 animate-spin" /> analisando…</span>
                          ) : spec?.status === "done" || spec?.status === "error" ? (
                            <button data-testid={`ws-item-spec-toggle-${m.numero_item}`} onClick={() => setExpandedSpec(open ? null : m.numero_item)} className="flex items-center gap-1">
                              {(() => { const [lbl, cls] = SPEC_STATUS[spec.ficha?.status] || SPEC_STATUS[spec.status] || SPEC_STATUS.atencao; return <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{lbl}</span>; })()}
                              <span className="text-[10px] text-sky-600 hover:underline">{open ? "ocultar" : "ver ficha"}</span>
                            </button>
                          ) : (
                            <button data-testid={`ws-item-spec-${m.numero_item}`} onClick={() => analyzeSpecs(m.numero_item)} className="flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:underline">🔎 Analisar</button>
                          )}
                        </td>
                        <td className="p-2 whitespace-nowrap text-right">
                          <button data-testid="ws-item-forn" onClick={() => openForn(m.numero_item)} className="text-xs font-semibold text-emerald-600 hover:underline">Fornecimento</button>
                          <button data-testid="ws-item-edit" onClick={() => openEditItem(m)} className="ml-2 text-xs font-semibold text-sky-600 hover:underline">Editar</button>
                          {m.manual_id && <button onClick={() => delItem(m)} className="ml-2 text-xs font-semibold text-rose-600 hover:underline">Excluir</button>}
                        </td>
                      </tr>
                      {open && spec && (
                        <tr><td colSpan={9} className="p-2">
                          {spec.status === "error"
                            ? <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">Falha na análise: {spec.error}. <button onClick={() => analyzeSpecs(m.numero_item)} className="font-semibold underline">Tentar novamente</button></div>
                            : <div className="flex flex-col gap-2"><FichaTecnica entry={spec} /><div className="flex justify-end"><button onClick={() => analyzeSpecs(m.numero_item)} className="text-[11px] font-semibold text-violet-600 hover:underline">🔄 Reanalisar item</button></div></div>}
                        </td></tr>
                      )}
                      </React.Fragment>
                    );})}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-xs text-slate-400">A compatibilidade é uma sugestão por similaridade — confirme manualmente. O sistema nunca presume compatibilidade jurídica.</p>

            {/* Fase 4 — Gerador de Cotação (Excel) */}
            <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="ws-cotacao">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Cotação para fornecedores (Excel)</div>
                  <p className="mt-0.5 max-w-xl text-xs text-slate-500">Gera uma planilha profissional com os itens + especificações da IA, com fórmulas de total e campos para o fornecedor preencher. Custos e margens internas nunca entram no arquivo.</p>
                </div>
                <button data-testid="ws-cotacao-gerar" onClick={gerarCotacao} disabled={genCot || !data.item_matches?.length} className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                  {genCot ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Gerar Cotação (.xlsx)
                </button>
              </div>
              {(ws.cotacoes || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {ws.cotacoes.map((c) => (
                    <div key={c.id} data-testid="ws-cotacao-row" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2.5">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">V{c.versao}</span>
                        <span className="font-medium text-slate-700">{c.nome}</span>
                        <span className="text-[11px] text-slate-400">{c.itens_count} item(ns) · {fmtDateTime(c.generated_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select data-testid="ws-cotacao-status" value={c.status} onChange={(e) => setCotacaoStatus(c.id, e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs">
                          {["gerada", "enviada", "respondida", "cancelada"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <a data-testid="ws-cotacao-download" href={`${api.defaults.baseURL}/opportunities/${id}/cotacao/${c.id}/xlsx`} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"><Download className="h-3.5 w-3.5" /> Baixar</a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {itemModal && (
              <div className="fixed inset-0 z-50 grid place-items-center p-4">
                <div className="absolute inset-0 bg-slate-900/50" onClick={() => setItemModal(null)} />
                <div className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
                  <h3 className="mb-1 font-display text-lg font-bold">{itemModal._override_numero ? `Editar item nº ${itemModal._override_numero}` : itemModal.id ? "Editar item manual" : "Adicionar item manualmente"}</h3>
                  {itemModal._override_numero && <p className="mb-3 text-xs text-amber-600">Este item foi importado automaticamente. A alteração será registrada no histórico.</p>}
                  {dupWarn && (
                    <div data-testid="ws-item-dup-warn" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                      Possível item duplicado: já existe um item com esse número/descrição. Deseja cadastrar mesmo assim?
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => setDupWarn(false)} className="rounded border border-amber-300 px-3 py-1 text-xs font-semibold">Cancelar</button>
                        <button data-testid="ws-item-dup-confirm" onClick={() => saveItem(true)} className="rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white">Cadastrar mesmo assim</button>
                      </div>
                    </div>
                  )}
                  <div className="grid max-h-[55vh] grid-cols-2 gap-2.5 overflow-y-auto">
                    {ITEM_FORM.map(([k, lbl, cls]) => (
                      <div key={k} className={cls || ""}>
                        <label className="mb-0.5 block text-[11px] font-semibold text-slate-600">{lbl}</label>
                        <input data-testid={`ws-item-f-${k}`} value={itemModal[k] ?? ""} disabled={k === "numero_item" && !!itemModal._override_numero} onChange={(e) => setItemModal({ ...itemModal, [k]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm disabled:bg-slate-100" />
                      </div>
                    ))}
                    {!itemModal._override_numero && (
                      <>
                        <div>
                          <label className="mb-0.5 block text-[11px] font-semibold text-slate-600">Origem do item</label>
                          <select data-testid="ws-item-f-origem" value={itemModal.origem ?? "manual"} onChange={(e) => setItemModal({ ...itemModal, origem: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                            {Object.entries(ORIGEM).filter(([k]) => k !== "manual_edit").map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="mb-0.5 block text-[11px] font-semibold text-slate-600">Fonte</label>
                          <select value={itemModal.fonte ?? ""} onChange={(e) => setItemModal({ ...itemModal, fonte: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                            <option value="">—</option>
                            {FONTE_OPCOES.map((f) => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button onClick={() => setItemModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
                    <button data-testid="ws-item-save" onClick={() => saveItem(false)} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar item</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "checklist" && (
          <div className="space-y-2" data-testid="ws-checklist">
            {ws.checklist.map((c, i) => (
              <div key={c.key} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                <input type="checkbox" data-testid={`ws-check-${c.key}`} checked={c.done} disabled={!c.aplicavel} onChange={() => toggleCheck(i)} className="h-4 w-4 rounded border-slate-300 text-sky-600 disabled:opacity-40" />
                <span className={`flex-1 text-sm ${!c.aplicavel ? "text-slate-400 line-through" : c.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{c.label}</span>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{c.categoria}</span>
                {!c.aplicavel && <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-400">NÃO APLICÁVEL</span>}
              </div>
            ))}
          </div>
        )}

        {tab === "cronograma" && (
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="ws-cronograma">
            <ol className="relative border-l-2 border-slate-100">
              {ws.cronograma.map((c, i) => (
                <li key={c.etapa} className="mb-5 ml-5">
                  <span className={`absolute -left-[9px] grid h-4 w-4 place-items-center rounded-full ${c.done ? "bg-emerald-500" : "bg-slate-300"}`}>
                    {c.done && <CheckCircle2 className="h-3 w-3 text-white" />}
                  </span>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{c.etapa}</div>
                      <div className="text-xs text-slate-400">{c.data ? fmtDateTime(c.data) : "sem data definida"}</div>
                    </div>
                    <button data-testid={`ws-etapa-${i}`} onClick={() => toggleEtapa(i)} className={`rounded px-2 py-1 text-[11px] font-semibold ${c.done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                      {c.done ? "Concluída" : "Marcar"}
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {tab === "historico" && (
          <div className="space-y-2" data-testid="ws-historico">
            {(!ws.history || ws.history.length === 0) ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Nenhum registro ainda.</div>
            ) : ws.history.map((h, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                <History className="mt-0.5 h-4 w-4 text-slate-400" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-800">{h.action}</div>
                  {h.detail && <div className="text-xs text-slate-500">{h.detail}</div>}
                  <div className="text-[11px] text-slate-400">{fmtDateTime(h.ts)} · {h.user}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "resultado" && (() => {
          const st = resultado.status_oficial || "EM ANDAMENTO";
          const isWin = WIN_STATUS.includes(st);
          const isLoss = LOSS_STATUS.includes(st);
          const vp = parseFloat(resultado.valor_proposta) || 0;
          const pv = parseFloat(resultado.preco_vencedor) || 0;
          const dif = (vp && pv) ? ((vp - pv) / pv) * 100 : null;
          const contrato = ws.contrato;
          const F = ({ label, k, type = "text" }) => (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
              <input data-testid={`ws-result-${k}`} type={type} value={resultado[k] ?? ""} onChange={(e) => setR(k, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
          );
          return (
            <div data-testid="ws-resultado" className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Status oficial</label>
                    <select data-testid="ws-result-status" value={st} onChange={(e) => setR("status_oficial", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                      {RESULT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <p className="mt-1 text-[11px] text-slate-400">"1º colocado" ≠ "ganha". O resultado oficial segue: 1º colocado → declarado vencedor → adjudicado → homologado.</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Valor estimado (edital)</label>
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">{brl(o.valor_estimado)}</div>
                  </div>
                  {F({ label: "Valor da sua proposta (R$)", k: "valor_proposta", type: "number" })}
                  {F({ label: "Valor final / homologado (R$)", k: "valor_final", type: "number" })}
                  {F({ label: "Empresa vencedora", k: "empresa_vencedora" })}
                  {F({ label: "Preço do vencedor (R$)", k: "preco_vencedor", type: "number" })}
                  {F({ label: "Margem estimada (%)", k: "margem_estimada", type: "number" })}
                  {F({ label: "Margem real (%)", k: "margem_real", type: "number" })}
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Concorrentes identificados</label>
                    <textarea data-testid="ws-result-concorrentes" value={resultado.concorrentes ?? ""} onChange={(e) => setR("concorrentes", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                  </div>
                </div>
                {dif != null && (
                  <div className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold ${dif > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                    Sua proposta ficou {dif > 0 ? `${dif.toFixed(1)}% acima` : `${Math.abs(dif).toFixed(1)}% abaixo`} do preço do vencedor.
                  </div>
                )}
                {isLoss && (
                  <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
                    <label className="mb-1 block text-xs font-semibold text-rose-700">Motivo da perda</label>
                    <select data-testid="ws-result-motivo" value={resultado.motivo_perda ?? ""} onChange={(e) => setR("motivo_perda", e.target.value)} className="w-full rounded-lg border border-rose-200 px-3 py-2 text-sm">
                      <option value="">Selecione</option>
                      {LOSS_MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <textarea value={resultado.motivo_perda_desc ?? ""} onChange={(e) => setR("motivo_perda_desc", e.target.value)} rows={2} placeholder="Descrição livre" className="mt-2 w-full rounded-lg border border-rose-200 px-3 py-2 text-sm" />
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid="ws-result-save" onClick={saveResultado} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">Salvar resultado</button>
                  {(WIN_STATUS.includes(st) || LOSS_STATUS.includes(st) || st === "1º COLOCADO" || st === "CANCELADA") && (
                    <button data-testid="ws-result-pipeline" onClick={() => changeStage(st)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Refletir status no Pipeline</button>
                  )}
                </div>
              </div>

              {isWin && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="ws-conversao">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 font-display text-sm font-bold text-emerald-800"><Trophy className="h-4 w-4" /> Operação comercial</div>
                      <p className="text-xs text-emerald-700">Licitação vencida — converta em operação comercial. Gera contas a receber no Financeiro e o fluxo até o recebimento.</p>
                    </div>
                    {!contrato && <button data-testid="ws-converter" onClick={converterComercial} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Converter em operação comercial</button>}
                  </div>
                  {contrato && (
                    <div className="mt-3">
                      <div className="mb-3 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-emerald-700">✓ Contas a receber criado no Financeiro · valor {brl(contrato.valor)}</div>
                      <ol className="relative ml-1 border-l-2 border-emerald-200">
                        {contrato.etapas.map((e, i) => (
                          <li key={e.etapa} className="mb-3 ml-4 flex items-center justify-between">
                            <span className={`text-sm ${e.done ? "font-semibold text-emerald-700" : "text-slate-600"}`}>{e.etapa}</span>
                            <button data-testid={`ws-contrato-etapa-${i}`} onClick={() => toggleContratoEtapa(i)} className={`rounded px-2 py-0.5 text-[11px] font-semibold ${e.done ? "bg-emerald-100 text-emerald-700" : "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>{e.done ? "Concluída" : "Marcar"}</button>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {tab === "edital" && (() => {
          const ea = ws.edital_analysis;
          const processing = ea?.status === "processing";
          const res = ea?.status === "done" ? ea.result : null;
          return (
            <div data-testid="ws-edital">
              <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800">Análise Inteligente do Edital</div>
                    <p className="text-xs text-slate-500">Clique em <strong>Analisar edital</strong> — o sistema busca os documentos oficiais direto no PNCP e a IA já começa. A IA extrai exigências, prazos e critérios com a <strong>fonte</strong> (arquivo/página) e nunca inventa informações.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {o.link_oficial && <a href={o.link_oficial} target="_blank" rel="noreferrer" data-testid="ws-edital-pncp" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50"><ExternalLink className="h-4 w-4" /> Abrir no PNCP</a>}
                    <button data-testid="ws-edital-fetch-pncp" onClick={fetchPncp} disabled={fetchingPncp || processing} className="flex items-center gap-2 rounded-lg border border-sky-200 px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-50">
                      {fetchingPncp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Buscar no PNCP
                    </button>
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      {uploadingEdital ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importar manualmente
                      <input data-testid="ws-edital-upload" type="file" className="hidden" onChange={uploadEdital} />
                    </label>
                    <button data-testid="ws-edital-analyze" onClick={analyzeEdital} disabled={processing}
                      className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                      {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />}
                      {res ? "Refazer análise" : "Analisar edital com IA"}
                    </button>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {(ws.edital_files || []).length === 0 ? (
                    <p className="text-sm text-slate-400">Nenhum arquivo ainda. Clique em <strong>Analisar edital com IA</strong> que buscamos os documentos no PNCP automaticamente — ou use <strong>Importar manualmente</strong>.</p>
                  ) : ws.edital_files.map((f) => (
                    <div key={f.id} data-testid="ws-edital-file" className="flex items-center gap-3 rounded-lg border border-slate-100 p-2.5">
                      {f.origem === "PNCP" && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">PNCP</span>}
                      <FileText className="h-4 w-4 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-700">{f.nome}</div>
                        <div className="text-[11px] text-slate-400">{fmtDateTime(f.uploaded_at)} · {(f.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <a href={`${api.defaults.baseURL}/opportunities/${id}/edital-files/${f.id}`} target="_blank" rel="noreferrer" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Download className="h-4 w-4" /></a>
                      <button onClick={() => deleteEdital(f.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                <span>📄 EDITAL = documento oficial</span><span>🤖 ANÁLISE IA = interpretação</span><span>💡 SUGESTÃO</span><span>👤 USUÁRIO</span>
              </div>

              {processing && <div data-testid="ws-edital-processing" className="rounded-xl border border-sky-200 bg-sky-50 p-6 text-center text-sm text-sky-700"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />Analisando o edital com IA… pode levar cerca de 1 minuto. A página atualiza sozinha.<div className="mt-3"><button data-testid="ws-edital-reset" onClick={resetEdital} className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100">Está demorando? Reiniciar análise</button></div></div>}
              {ea?.status === "error" && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">Não foi possível analisar: {ea.error}</div>}

              {res && (
                <div className="space-y-4" data-testid="ws-edital-result">
                  {ea.nota && <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">⚠️ {ea.nota}</div>}
                  <div className="flex justify-end"><button data-testid="ws-hab-from-edital" onClick={habFromEdital} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">Gerar Matriz de Habilitação a partir do edital →</button></div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="mb-3 text-sm font-semibold text-slate-800">📄 Informações extraídas do edital</div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {Object.entries(res.campos || {}).map(([k, v]) => {
                        const val = v && typeof v === "object" ? v.valor : v;
                        const fonte = v && typeof v === "object" ? v.fonte : null;
                        const nl = !val || String(val).toUpperCase().includes("NÃO LOCALIZ");
                        return (
                          <div key={k} className="rounded-lg border border-slate-100 p-2.5">
                            <div className="text-[11px] font-semibold uppercase text-slate-400">{CAMPO_LABELS[k] || k}</div>
                            <div className={`text-sm ${nl ? "italic text-slate-400" : "text-slate-800"}`}>{val || "INFORMAÇÃO NÃO LOCALIZADA"}</div>
                            {!nl && <Fonte fonte={fonte} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {(res.exigencias || []).length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="mb-3 text-sm font-semibold text-slate-800">Exigências identificadas ({res.exigencias.length})</div>
                      <div className="space-y-2">
                        {res.exigencias.map((e, i) => (
                          <div key={i} className="rounded-lg border border-slate-100 p-2.5">
                            <div className="flex flex-wrap items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-sky-600" /><span className="text-sm font-medium text-slate-700">{e.requisito}</span>{e.sicaf_nivel && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">SICAF {e.sicaf_nivel}</span>}</div>
                            {e.documento_necessario && <div className="text-xs text-slate-500">Documento: {e.documento_necessario}</div>}
                            <Fonte fonte={e.fonte} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(res.pontos_atencao || []).length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="mb-3 text-sm font-semibold text-slate-800">Pontos de atenção</div>
                      <ul className="space-y-2">
                        {res.pontos_atencao.map((p, i) => (
                          <li key={i} className="text-sm text-slate-700">• {p.texto}<Fonte fonte={p.fonte} /></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {!res && !processing && ea?.status !== "error" && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Anexe o PDF do edital e clique em <strong>Analisar edital com IA</strong>.</div>
              )}
            </div>
          );
        })()}
        {tab === "compliance" && (() => {
          const cp = ws.compliance;
          const proc = cp?.status === "processing";
          const res = cp?.status === "done" ? cp.result : null;
          return (
            <div data-testid="ws-compliance">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <div className="max-w-xl">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><ShieldAlert className="h-4 w-4 text-sky-600" /> Agente de Compliance operacional</div>
                  <p className="mt-1 text-xs text-slate-500">A IA lê o edital/TR e avalia regras de <strong>entrega, logística, prazo, subcontratação e venda à ordem/entrega direta</strong>, com a fonte (cláusula/página). Nunca inventa — temas silentes ficam ⚪. Confirme sempre no edital.</p>
                </div>
                <button data-testid="ws-compliance-analyze" onClick={analyzeCompliance} disabled={proc} className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                  {proc ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}{res ? "Refazer análise" : "Analisar compliance"}
                </button>
              </div>
              {proc && <div data-testid="ws-compliance-processing" className="rounded-xl border border-sky-200 bg-sky-50 p-6 text-center text-sm text-sky-700"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />Analisando compliance… a página atualiza sozinha.</div>}
              {cp?.status === "error" && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">Falha: {cp.error}</div>}
              {res && (
                <div className="space-y-4" data-testid="ws-compliance-result">
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="mb-2 flex items-center gap-2"><ComplianceBadge status={res.status_geral} /><span className="text-[11px] text-slate-400">{cp.com_edital ? "Baseado no edital anexado" : "Sem edital — anexe para análise completa"}</span></div>
                    <p className="text-sm text-slate-600">{res.resumo}</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[["venda_a_ordem", "Venda à ordem / Entrega direta", res.venda_a_ordem, "permitido"], ["entrega_direta_viavel", "Entrega direta viável", res.entrega_direta_viavel, "viavel"]].map(([k, lbl, obj, boolKey]) => obj && (
                      <div key={k} className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="text-xs font-bold text-slate-700">{lbl}</div>
                        <div className="mt-1 text-sm font-semibold">{obj[boolKey] === true ? <span className="text-emerald-600">✓ Admitido</span> : obj[boolKey] === false ? <span className="text-rose-600">✕ Não admitido / vedado</span> : <span className="text-slate-400">⚪ Não identificado</span>}</div>
                        {obj.observacao && <p className="mt-1 text-xs text-slate-500">{obj.observacao}</p>}
                        <Fonte fonte={obj.fonte} />
                      </div>
                    ))}
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">Regras operacionais identificadas</div>
                    {(res.regras || []).length === 0 ? (
                      <p className="p-4 text-sm text-slate-400">Nenhuma regra operacional específica localizada no texto analisado.</p>
                    ) : (res.regras || []).map((r, i) => (
                      <div key={i} className="flex items-start gap-3 border-b border-slate-50 p-3 last:border-0">
                        <span className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${DOT[r.status] || "bg-slate-300"}`} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-800">{r.tema}</div>
                          {r.exigencia && <div className="text-xs text-slate-600">{r.exigencia}</div>}
                          {r.impacto && <div className="mt-0.5 text-[11px] text-slate-400">Impacto: {r.impacto}</div>}
                          <Fonte fonte={r.fonte} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400">Análise interna de apoio à decisão. Não substitui a leitura integral do edital nem configura parecer jurídico.</p>
                </div>
              )}
              {!res && !proc && cp?.status !== "error" && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Clique em <strong>Analisar compliance</strong> — buscamos os documentos no PNCP automaticamente e avaliamos a viabilidade operacional do fornecimento.</div>
              )}
            </div>
          );
        })()}
        {tab === "habilitacao" && (
          <div data-testid="ws-habilitacao">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-xl text-xs text-slate-500">Exigências típicas (Lei 14.133/2021) por nível SICAF, cruzadas automaticamente com o Cofre Documental. <strong>Confirme sempre no edital</strong> — a IA nunca declara suficiência jurídica automática.</p>
              <button data-testid="ws-hab-analyze" onClick={analyzeHab} disabled={analyzingHab} className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {analyzingHab ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {ws.habilitacao ? "Refazer análise" : "Analisar habilitação com IA"}
              </button>
            </div>
            {!ws.habilitacao ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
                Clique em <strong>Analisar habilitação com IA</strong> para gerar a Matriz de Habilitação e cruzar com o Cofre Documental.
                <div className="mt-1 text-xs text-slate-400">Cadastre seus documentos em Configurações → Documentos & Certidões.</div>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="p-2">Requisito</th><th className="p-2">SICAF</th><th className="p-2">Fonte</th>
                      <th className="p-2">Doc. necessário</th><th className="p-2">Encontrado</th>
                      <th className="p-2">Status</th><th className="p-2">Observação / Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ws.habilitacao.rows.map((r, i) => (
                      <tr key={i} data-testid="ws-hab-row" className="align-top hover:bg-slate-50">
                        <td className="p-2 font-medium text-slate-700">{r.requisito}<div className="text-[11px] font-normal text-slate-400">{r.categoria}</div></td>
                        <td className="p-2 text-slate-500">{r.sicaf_nivel || "—"}</td>
                        <td className="p-2 text-[11px] text-slate-400">{r.fonte}</td>
                        <td className="p-2 text-xs text-slate-600">{r.documento_necessario}</td>
                        <td className="p-2 text-xs text-slate-600">{r.documento_encontrado || <span className="text-slate-300">—</span>}{r.validade && <div className="text-[10px] text-slate-400">val. {new Date(r.validade).toLocaleDateString("pt-BR")}</div>}</td>
                        <td className="p-2">
                          <select data-testid={`ws-hab-status-${i}`} value={r.status} onChange={(e) => setHabStatus(i, e.target.value)}
                            className={`rounded-md border-0 px-2 py-1 text-[11px] font-bold ${DOT_TEXT[r.status]} bg-slate-50`}>
                            {["verde", "amarelo", "vermelho", "laranja", "cinza"].map((s) => <option key={s} value={s}>{HAB_LABEL[s]}</option>)}
                          </select>
                        </td>
                        <td className="p-2 text-xs text-slate-500">{r.observacao}<div className="text-[11px] font-semibold text-slate-600">→ {r.acao}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {tab === "documentos" && (
          <div data-testid="ws-documentos" className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-800">Anexos desta licitação</div>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  {uploadingEdital ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Anexar
                  <input type="file" className="hidden" onChange={uploadEdital} />
                </label>
              </div>
              <div className="mt-3 space-y-2">
                {(ws.edital_files || []).length === 0 ? <p className="text-sm text-slate-400">Nenhum anexo.</p> : ws.edital_files.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 rounded-lg border border-slate-100 p-2 text-sm">
                    <FileText className="h-4 w-4 text-slate-400" /><span className="flex-1 truncate text-slate-700">{f.nome}</span>
                    <a href={`${api.defaults.baseURL}/opportunities/${id}/edital-files/${f.id}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-600"><Download className="h-4 w-4" /></a>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-800">Cofre Documental da empresa</div>
              <p className="text-xs text-slate-500">Documentos reutilizáveis por nível SICAF.</p>
              <div className="mt-3 space-y-1.5">
                {(data.company_documents || []).length === 0 ? (
                  <p className="text-sm text-slate-400">Nenhum documento no Cofre. Cadastre em Configurações → Documentos & Certidões.</p>
                ) : data.company_documents.slice(0, 12).map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-sm">
                    <span className={`h-2 w-2 rounded-full ${d.status === "vencido" ? "bg-rose-500" : d.status === "proximo" ? "bg-amber-500" : "bg-emerald-500"}`} />
                    <span className="flex-1 truncate text-slate-700">{d.nome}</span>
                    <span className="text-[11px] text-slate-400">{d.sicaf_nivel || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {tab === "fornecedores" && (
          <div data-testid="ws-fornecedores">
            <ItensCotacao id={id} data={data} onUpdated={(d) => { setData(d); setPrec(d.workspace?.precificacao?.itens || {}); setFornCfg(d.workspace?.item_fornecimento || {}); }} />
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-slate-500">Cotações de fornecedores para esta licitação — preço, frete, prazo e condições.</p>
              <button data-testid="ws-forn-add" onClick={() => setFornModal({})} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">+ Nova cotação</button>
            </div>
            {(ws.fornecedores || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Nenhuma cotação registrada.</div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500"><tr>
                    <th className="p-2">Fornecedor</th><th className="p-2">Produto</th><th className="p-2 text-right">Preço</th><th className="p-2 text-right">Frete</th><th className="p-2">Prazo</th><th className="p-2">Item</th><th className="p-2"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {ws.fornecedores.map((f) => (
                      <tr key={f.id} data-testid="ws-forn-row" className="hover:bg-slate-50">
                        <td className="p-2 font-medium text-slate-700">{f.nome}<div className="text-[11px] text-slate-400">{f.cnpj || ""} {f.contato || ""}</div></td>
                        <td className="p-2 text-slate-600">{f.produto || "—"}</td>
                        <td className="p-2 text-right font-mono">{f.preco ? brl(f.preco) : "—"}</td>
                        <td className="p-2 text-right font-mono">{f.frete ? brl(f.frete) : "—"}</td>
                        <td className="p-2 text-slate-500">{f.prazo || "—"}</td>
                        <td className="p-2 text-slate-500">{f.item_numero || "—"}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <button onClick={() => setFornModal(f)} className="text-xs font-semibold text-sky-600 hover:underline">Editar</button>
                          <button onClick={() => delForn(f.id)} className="ml-2 text-xs font-semibold text-rose-600 hover:underline">Excluir</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {fornModal && (
              <div className="fixed inset-0 z-50 grid place-items-center p-4">
                <div className="absolute inset-0 bg-slate-900/50" onClick={() => setFornModal(null)} />
                <div className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
                  <h3 className="mb-4 font-display text-lg font-bold">{fornModal.id ? "Editar cotação" : "Nova cotação"}</h3>
                  <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto pr-1">
                    <div className="col-span-2">
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Fornecedor cadastrado</label>
                      <select data-testid="ws-forn-supplier" value={fornModal.supplier_id ?? ""} onChange={(e) => { const s = allSups.find((x) => x.id === e.target.value); setFornModal({ ...fornModal, supplier_id: e.target.value || null, ...(s ? { nome: s.nome_fantasia || s.razao_social, cnpj: s.cnpj || "", contato: s.contato || s.email || "" } : {}) }); }} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                        <option value="">— Selecione da base de Fornecedores —</option>
                        {allSups.map((s) => <option key={s.id} value={s.id}>{s.nome_fantasia || s.razao_social}</option>)}
                      </select>
                    </div>
                    {[["nome", "Fornecedor *", "col-span-2"], ["cnpj", "CNPJ", ""], ["contato", "Contato", ""], ["produto", "Produto", "col-span-2"], ["preco", "Preço (R$)", ""], ["frete", "Frete (R$)", ""], ["prazo", "Prazo de entrega", ""], ["condicao_pagamento", "Condição de pagamento", ""], ["item_numero", "Item nº (vincula à proposta)", ""], ["observacao", "Observação", "col-span-2"]].map(([k, lbl, cls]) => (
                      <div key={k} className={cls}>
                        <label className="mb-1 block text-xs font-semibold text-slate-600">{lbl}</label>
                        <input data-testid={`ws-forn-${k}`} value={fornModal[k] ?? ""} onChange={(e) => setFornModal({ ...fornModal, [k]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      </div>
                    ))}
                    <div className="col-span-2 mt-1 rounded-lg bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Dados técnicos do produto (usados na proposta)</div>
                      <p className="mb-2 text-[11px] text-slate-400">Pertencem a esta cotação/item — não ao cadastro do fornecedor. "Marca exigida" ≠ "Marca de referência".</p>
                      <div className="grid grid-cols-2 gap-3">
                        {[["marca_exigida", "Marca exigida"], ["marca_referencia", "Marca de referência"], ["fabricante", "Fabricante"], ["modelo", "Modelo / Referência"], ["dimensoes", "Dimensões"], ["material", "Material"], ["cor", "Cor"], ["capacidade", "Capacidade"], ["embalagem", "Embalagem"]].map(([k, lbl]) => (
                          <div key={k}>
                            <label className="mb-1 block text-xs font-semibold text-slate-600">{lbl}</label>
                            <input data-testid={`ws-forn-${k}`} value={fornModal[k] ?? ""} onChange={(e) => setFornModal({ ...fornModal, [k]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                          </div>
                        ))}
                        <div className="col-span-2">
                          <label className="mb-1 block text-xs font-semibold text-slate-600">Especificações técnicas</label>
                          <textarea data-testid="ws-forn-especificacoes_tecnicas" rows={2} value={fornModal.especificacoes_tecnicas ?? ""} onChange={(e) => setFornModal({ ...fornModal, especificacoes_tecnicas: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 mt-1 rounded-lg border border-sky-100 bg-sky-50/50 p-3">
                      <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-sky-700">🔗 Validação do produto por link</div>
                      <p className="mb-2 text-[11px] text-slate-500">Cole a URL onde encontrou o produto (fabricante, distribuidor, marketplace…). A IA compara com o que o edital exige. Link, preço e fornecedor de origem NÃO vão para a proposta.</p>
                      <div className="flex gap-2">
                        <input data-testid="ws-forn-link_produto" value={fornModal.link_produto ?? ""} onChange={(e) => setFornModal({ ...fornModal, link_produto: e.target.value })} placeholder="https://…" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                        <button data-testid="ws-forn-analisar" onClick={analisarLink} disabled={analisando} className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">{analisando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analisar produto</button>
                      </div>
                      {fornModal.analise_link && <AnaliseLink a={fornModal.analise_link} onUsar={usarProduto} onReanalisar={analisarLink} reanalisando={analisando} />}
                    </div>
                  </div>
                  <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setFornModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
                    <button data-testid="ws-forn-save" onClick={saveForn} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === "precificacao" && (
          <div data-testid="ws-precificacao">
            {(!data.item_matches || data.item_matches.length === 0) ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Nenhum item carregado.</div>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs text-slate-500">Custo + frete + impostos + outros = custo total. Custo total × (1 + margem%) = preço sugerido.</p>
                  <button data-testid="ws-prec-save" onClick={savePrec} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">Salvar precificação</button>
                </div>
                <div className="space-y-2">
                  {data.item_matches.map((m) => {
                    const n = String(m.numero_item);
                    const p = prec[n] || {};
                    const ct = (+p.custo_produto || 0) + (+p.frete || 0) + (+p.impostos || 0) + (+p.outros || 0);
                    const ps = ct * (1 + (+p.margem_pct || 0) / 100);
                    const semEstimado = !m.valor_unitario || +m.valor_unitario <= 0;
                    const acima = !semEstimado && ps > +m.valor_unitario;
                    const set = (field, val) => {
                      const np = { ...(prec[n] || {}), [field]: val };
                      const c = (+np.custo_produto || 0) + (+np.frete || 0) + (+np.impostos || 0) + (+np.outros || 0);
                      np.custo_total = c; np.preco_sugerido = c * (1 + (+np.margem_pct || 0) / 100);
                      setPrec({ ...prec, [n]: np });
                    };
                    return (
                      <div key={n} data-testid="ws-prec-row" className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="mb-2 text-sm font-medium text-slate-700">Item {m.numero_item} · {m.descricao} <span className="text-xs text-slate-400">{semEstimado ? "(valor estimado não informado)" : `(estimado ${brl(m.valor_unitario)})`}</span></div>
                        {p.cotacao_id && (() => { const c = (ws.fornecedores || []).find((f) => f.id === p.cotacao_id); return c ? <div data-testid={`ws-prec-${n}-linked`} className="mb-2 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">🔗 Custo e frete vinculados à cotação de {c.nome}</div> : null; })()}
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                          {[["custo_produto", "Custo"], ["frete", "Frete"], ["impostos", "Impostos"], ["outros", "Outros"], ["margem_pct", "Margem %"]].map(([k, lbl]) => (
                            <div key={k}>
                              <label className="mb-0.5 block text-[10px] font-semibold uppercase text-slate-400">{lbl}</label>
                              <input data-testid={`ws-prec-${n}-${k}`} type="number" value={p[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                            </div>
                          ))}
                          <div>
                            <label className="mb-0.5 block text-[10px] font-semibold uppercase text-slate-400">Preço sugerido</label>
                            <div className={`rounded-lg px-2 py-1.5 text-sm font-bold ${acima ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{brl(ps)}</div>
                          </div>
                        </div>
                        {acima && <div className="mt-1.5 text-[11px] font-semibold text-rose-600">⚠️ Preço calculado acima do valor estimado do item.</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
        {tab === "proposta" && (
          <PropostaModule id={id} setParentTab={setTab} navigate={navigate} />
        )}
      </div>

      {fornEdit && (() => {
        const d = fornEdit.draft; const num = fornEdit.numero;
        const relForn = (ws.fornecedores || []).filter((f) => !f.item_numero || String(f.item_numero) === num);
        const fields = d.modelo === "entrega_direta" ? FORN_DIRETA : d.modelo === "estoque_proprio" ? FORN_ESTOQUE : [];
        return (
          <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="ws-forn-modal">
            <div className="absolute inset-0 bg-slate-900/50" onClick={() => setFornEdit(null)} />
            <div className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="mb-1 font-display text-lg font-bold">Fornecimento do item nº {num}</h3>
              <p className="mb-3 text-xs text-slate-400">Camada interna da operação — não aparece na proposta ao órgão.</p>
              <div className="mb-3">
                <label className="mb-1 block text-[11px] font-semibold text-slate-600">Modelo de abastecimento</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(MODELO).map(([k, v]) => (
                    <button key={k} data-testid={`ws-forn-modelo-${k}`} onClick={() => setFD("modelo", k)} className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${d.modelo === k ? `${v[1]} ring-2 ring-sky-400 ring-offset-1` : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{v[0]}</button>
                  ))}
                </div>
              </div>
              <div className="grid max-h-[50vh] grid-cols-2 gap-2.5 overflow-y-auto">
                <div>
                  <label className="mb-0.5 block text-[11px] font-semibold text-slate-600">Canal de aquisição</label>
                  <select data-testid="ws-forn-canal" value={d.canal ?? ""} onChange={(e) => setFD("canal", e.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                    <option value="">—</option>{CANAIS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-0.5 block text-[11px] font-semibold text-slate-600">Status logístico</label>
                  <select data-testid="ws-forn-status" value={d.status_logistico ?? ""} onChange={(e) => setFD("status_logistico", e.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                    <option value="">—</option>{STATUS_LOG.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {fields.map(([k, lbl]) => (
                  <div key={k} className={k === "endereco_entrega" || k === "obs" ? "col-span-2" : ""}>
                    <label className="mb-0.5 block text-[11px] font-semibold text-slate-600">{lbl}</label>
                    <input type={fornType(k)} value={d[k] ?? ""} onChange={(e) => setFD(k, e.target.value)} className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <div className="mb-1 text-xs font-semibold text-slate-600">Comparar fornecedores{d.fornecedor_nome && <span className="text-emerald-700"> · selecionado: {d.fornecedor_nome}</span>}</div>
                {relForn.length === 0 ? <p className="text-xs text-slate-400">Nenhuma cotação. Cadastre na aba Fornecedores.</p> : (
                  <div className="space-y-1">
                    {relForn.map((f) => (
                      <div key={f.id} className="flex items-center justify-between rounded border border-slate-100 p-2 text-xs">
                        <span className="text-slate-600">{f.nome} · custo {f.preco ? brl(f.preco) : "—"} · frete {f.frete ? brl(f.frete) : "—"} · prazo {f.prazo || "—"}</span>
                        <button data-testid={`ws-usar-forn-${f.id}`} onClick={() => usarFornecedor(f)} className="rounded bg-emerald-600 px-2 py-0.5 font-semibold text-white">Usar</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setFornEdit(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
                <button data-testid="ws-forn-save" onClick={saveForn2} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
              </div>
            </div>
          </div>
        );
      })()}
      {saving && <div className="fixed bottom-4 right-4 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Salvando…</div>}
    </div>
  );
}

const COMPAT_META = {
  verde: ["🟢 COMPATÍVEL", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  amarelo: ["🟡 COMPATÍVEL COM RESSALVAS", "bg-amber-50 text-amber-700 border-amber-200"],
  vermelho: ["🔴 INCOMPATÍVEL", "bg-rose-50 text-rose-700 border-rose-200"],
  branco: ["⚪ NÃO FOI POSSÍVEL VALIDAR", "bg-slate-50 text-slate-500 border-slate-200"],
};
const REC_META = {
  apto: ["🟢 APTO PARA COTAÇÃO", "text-emerald-700"],
  conferencia: ["🟡 REQUER CONFERÊNCIA", "text-amber-700"],
  nao_recomendado: ["🔴 NÃO RECOMENDADO PARA ESTE ITEM", "text-rose-700"],
};
const RES_ICON = { verde: "🟢", amarelo: "🟡", vermelho: "🔴", pendente: "⚠️" };

function AnaliseLink({ a, onUsar, onReanalisar, reanalisando }) {
  const [cLbl, cCls] = COMPAT_META[a.status_compatibilidade] || COMPAT_META.branco;
  const [rLbl, rCls] = REC_META[a.recomendacao] || REC_META.conferencia;
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="ws-forn-analise">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${cCls}`} data-testid="ws-forn-analise-status">{cLbl}</span>
        <span className="text-[10px] text-slate-400">v{a.versao} · {fmtDateTime(a.at)} · {a.user}</span>
      </div>
      {a.resumo && <p className="text-xs text-slate-600">{a.resumo}</p>}
      {(a.matriz || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead><tr className="border-b border-slate-200 text-left text-slate-400"><th className="py-1 pr-2">Requisito</th><th className="pr-2">Exigido</th><th className="pr-2">Encontrado</th><th className="pr-2">Res.</th></tr></thead>
            <tbody>
              {a.matriz.map((m, i) => (
                <tr key={i} className="border-b border-slate-100 align-top" data-testid="ws-forn-analise-row">
                  <td className="py-1 pr-2 font-medium text-slate-700">{m.requisito}</td>
                  <td className="pr-2 text-slate-500">{m.exigido || "—"}</td>
                  <td className="pr-2 text-slate-600" title={m.fonte || ""}>{m.encontrado || "NÃO INFORMADO"}</td>
                  <td className="pr-2">{RES_ICON[m.resultado] || "⚠️"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(a.riscos || []).length > 0 && (
        <div className="rounded-lg bg-rose-50 p-2" data-testid="ws-forn-analise-riscos">
          <div className="text-[11px] font-bold text-rose-700">⚠️ Riscos identificados</div>
          <ul className="ml-4 list-disc text-[11px] text-rose-600">{a.riscos.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
        <div className={`text-xs font-bold ${rCls}`}>{rLbl}</div>
        <div className="flex gap-2">
          <button data-testid="ws-forn-reanalisar" onClick={onReanalisar} disabled={reanalisando} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-60">🔄 Reanalisar</button>
          <button data-testid="ws-forn-usar-produto" onClick={onUsar} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">Usar produto nesta cotação</button>
        </div>
      </div>
      <p className="text-[10px] text-slate-400">Análise interna de compatibilidade — não garante aceitação jurídica pela Administração. Nunca preenche especificação por suposição.</p>
    </div>
  );
}
